import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const CONTENT = {
  FR: {
    meta: 'Le composite card : tout ce que vous devez savoir',
    hero: {
      tag: 'LE GUIDE COMPLET',
      title: 'C\'est quoi un',
      titleAccent: 'Composite Card ?',
      sub: 'Le document de référence de tout mannequin ou comédien professionnel. On vous explique tout.',
      cta: 'CRÉER MON COMPOSITE GRATUITEMENT',
    },
    intro: {
      title: 'La carte de visite du mannequin',
      text1: 'Le composite card, aussi appelé comp card ou zed card, est le document de présentation professionnel utilisé par les mannequins, comédiens et artistes pour se présenter aux agences, directeurs de casting et clients.',
      text2: 'Contrairement à un simple book photo, le composite card regroupe l\'essentiel en un seul document : photos, mensurations, coordonnées. C\'est le premier document qu\'une agence demande, et souvent le seul qu\'un client consulte avant un casting.',
    },
    recto: {
      tag: 'RECTO',
      title: 'La face principale',
      text: 'Le recto, c\'est la première impression. Une grande photo principale occupe l\'essentiel de la page, accompagnée des informations d\'identité : prénom, nom, agence et coordonnées. C\'est la face que le client voit en premier.',
      photo: 'PHOTO RECTO',
    },
    verso: {
      tag: 'VERSO',
      title: 'La face des détails',
      text: 'Le verso présente plusieurs photos de styles différents, portrait, mode, sport, beauté, pour montrer la polyvalence du modèle. Les mensurations ou compétences figurent en bandeau, pour que le casting voit tout en un coup d\'œil.',
      photo: 'PHOTO VERSO',
    },
    why: {
      title: 'Pourquoi en avoir un ?',
      cards: [
        { icon: '📋', title: 'Castings & agences', text: 'Toutes les agences et directeurs de casting demandent un composite card à jour avant tout rendez-vous.' },
        { icon: '⚡', title: 'Premier impact', text: 'En quelques secondes, le client voit vos meilleurs clichés et vos informations clés.' },
        { icon: '🌍', title: 'Standard international', text: 'Le format composite card est utilisé partout dans le monde. Paris, Milan, New York, Tokyo. Un standard universel.' },
        { icon: '🔄', title: 'Toujours à jour', text: 'Avec Compify, mettez à jour votre composite en quelques minutes et téléchargez un nouveau PDF immédiatement.' },
      ],
    },
    diff: {
      title: 'Mannequin ou Comédien ?',
      sub: 'Le composite card s\'adapte selon votre métier',
      mannequin: {
        label: 'MANNEQUIN',
        points: ['Grande photo principale pleine page', 'Mensurations complètes (taille, poitrine, hanches...)', 'Plusieurs styles de photos au verso', 'Nom de l\'agence et coordonnées', 'Format A5 recto-verso'],
      },
      comedien: {
        label: 'COMÉDIEN',
        points: ['Photo principale et photos de jeu', 'Compétences (langues, voix, chant...)', 'Plusieurs registres au verso', 'Agent et coordonnées', 'Format A5 recto-verso'],
      },
    },
    faq: {
      title: 'Questions fréquentes',
      items: [
        { q: 'Quel format pour un composite card ?', a: 'Le format standard est le A5 (148×210mm) imprimé recto-verso. C\'est le format universel accepté par toutes les agences dans le monde.' },
        { q: 'Combien de photos sur un composite ?', a: 'En général 3 à 6 photos : une grande photo principale au recto, et 2 à 4 photos de styles variés au verso.' },
        { q: 'Comment imprimer son composite ?', a: 'Téléchargez votre PDF depuis Compify et faites-le imprimer chez un imprimeur en ligne (Vistaprint, Pixartprinting...) ou dans une imprimerie locale. Privilégiez le papier couché 350g.' },
        { q: 'À quelle fréquence mettre à jour son composite ?', a: 'Idéalement tous les 6 à 12 mois, ou dès que vous avez de nouvelles photos professionnelles ou que vos mensurations changent.' },
      ],
    },
    cta: {
      title: 'Créez votre composite card',
      titleAccent: 'gratuitement',
      sub: 'En moins de 5 minutes. Sans inscription. Téléchargement PDF immédiat.',
      btn: 'CRÉER MON COMPOSITE →',
    },
  },
  EN: {
    meta: 'The composite card: everything you need to know',
    hero: {
      tag: 'THE COMPLETE GUIDE',
      title: 'What is a',
      titleAccent: 'Composite Card?',
      sub: 'The go-to document for every professional model or actor. Here is everything you need to know.',
      cta: 'CREATE MY COMPOSITE FOR FREE',
    },
    intro: {
      title: 'The model\'s business card',
      text1: 'A composite card, also known as a comp card or zed card, is the professional presentation document used by models, actors and artists to introduce themselves to agencies, casting directors and clients.',
      text2: 'Unlike a simple photo book, the composite card brings together the essentials in one document: photos, measurements, contact details. It is the first document an agency requests, and often the only one a client looks at before a casting.',
    },
    recto: {
      tag: 'FRONT',
      title: 'The main face',
      text: 'The front is the first impression. One large main photo takes up most of the page, alongside identity information: first name, last name, agency and contact details. This is the side the client sees first.',
      photo: 'FRONT PHOTO',
    },
    verso: {
      tag: 'BACK',
      title: 'The detail face',
      text: 'The back features several photos in different styles, portrait, fashion, sport, beauty, to showcase the model\'s versatility. Measurements or skills appear in a banner so casting directors can take it all in at a glance.',
      photo: 'BACK PHOTO',
    },
    why: {
      title: 'Why do you need one?',
      cards: [
        { icon: '📋', title: 'Castings & agencies', text: 'All agencies and casting directors ask for an up-to-date composite card before any appointment.' },
        { icon: '⚡', title: 'First impact', text: 'In seconds, the client sees your best shots and key information.' },
        { icon: '🌍', title: 'International standard', text: 'The composite card format is used all over the world. Paris, Milan, New York, Tokyo. A true universal standard.' },
        { icon: '🔄', title: 'Always up to date', text: 'With Compify, update your composite in minutes and download a new PDF right away.' },
      ],
    },
    diff: {
      title: 'Model or Actor?',
      sub: 'The composite card adapts to your profession',
      mannequin: {
        label: 'MODEL',
        points: ['Large full-page main photo', 'Full measurements (height, bust, hips...)', 'Multiple photo styles on the back', 'Agency name and contact details', 'A5 double-sided format'],
      },
      comedien: {
        label: 'ACTOR',
        points: ['Main photo and performance shots', 'Skills (languages, voice, singing...)', 'Multiple registers on the back', 'Agent and contact details', 'A5 double-sided format'],
      },
    },
    faq: {
      title: 'Frequently asked questions',
      items: [
        { q: 'What size is a composite card?', a: 'The standard format is A5 (148×210mm) printed double-sided. This is the universal format accepted by all agencies worldwide.' },
        { q: 'How many photos on a composite card?', a: 'Usually 3 to 6 photos: one large main photo on the front, and 2 to 4 photos in varied styles on the back.' },
        { q: 'How do I print my composite card?', a: 'Download your PDF from Compify and have it printed at an online printer (Vistaprint, Pixartprinting...) or a local print shop. We recommend 350g coated paper.' },
        { q: 'How often should I update my composite?', a: 'Ideally every 6 to 12 months, or whenever you have new professional photos or your measurements change.' },
      ],
    },
    cta: {
      title: 'Create your composite card',
      titleAccent: 'for free',
      sub: 'In less than 5 minutes. No sign-up required. Instant PDF download.',
      btn: 'CREATE MY COMPOSITE →',
    },
  },
};

export default function CompositeCard() {
  const [siteLang, setSiteLang] = useState('FR');
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();
  const t = CONTENT[siteLang];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0f0f0f', fontFamily: 'Arial, sans-serif', color: '#fff' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ backgroundColor: '#0f0f0f', padding: '80px 24px', textAlign: 'center', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '20px' }}>{t.hero.tag}</span>
          <h1 style={{ margin: '0 0 20px', fontSize: '52px', fontWeight: '900', letterSpacing: '2px', lineHeight: 1.1 }}>
            {t.hero.title} <span style={{ color: '#FF0066' }}>{t.hero.titleAccent}</span>
          </h1>
          <p style={{ color: '#666', fontSize: '16px', lineHeight: 1.8, margin: '0 0 40px', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>{t.hero.sub}</p>
          <button onClick={() => navigate('/editeur')} style={{
            backgroundColor: '#FF0066', color: '#fff', border: 'none',
            padding: '16px 40px', fontSize: '12px', fontWeight: '700',
            letterSpacing: '2px', cursor: 'pointer',
          }}>{t.hero.cta}</button>
        </div>
      </section>

      {/* INTRO */}
      <section style={{ padding: '80px 24px', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '16px' }}>COMPOSITE CARD</span>
            <h2 style={{ margin: '0 0 24px', fontSize: '32px', fontWeight: '900', letterSpacing: '1px', lineHeight: 1.2 }}>{t.intro.title}</h2>
            <p style={{ color: '#888', fontSize: '14px', lineHeight: 1.9, margin: '0 0 16px' }}>{t.intro.text1}</p>
            <p style={{ color: '#888', fontSize: '14px', lineHeight: 1.9, margin: 0 }}>{t.intro.text2}</p>
          </div>
          {/* Photo placeholder intro */}
          <div style={{ height: '400px', overflow: 'hidden' }}>
            <img src="/agence.png" alt="Composite card" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>
      </section>

      {/* RECTO / VERSO */}
      <section style={{ padding: '80px 24px', backgroundColor: '#0a0a0a', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '12px' }}>FORMAT A5</span>
            <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '900', letterSpacing: '1px' }}>Recto & Verso</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
            {[
              { data: t.recto, isRecto: true },
              { data: t.verso, isRecto: false },
            ].map(({ data, isRecto }) => (
              <div key={data.tag} style={{ backgroundColor: '#1a1a1a', padding: '0', overflow: 'hidden', border: '1px solid #2a2a2a' }}>
                {/* Photo placeholder */}
                <div style={{ height: '280px', overflow: 'hidden', borderBottom: '1px solid #2a2a2a' }}>
                  <img
                    src={isRecto ? '/compo-recto.jpg' : '/compo-verso.jpg'}
                    alt={data.tag}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '28px' }}>
                  <span style={{ color: '#FF0066', fontSize: '9px', letterSpacing: '3px', fontWeight: '700' }}>{data.tag}</span>
                  <h3 style={{ margin: '8px 0 12px', fontSize: '20px', fontWeight: '900', letterSpacing: '1px' }}>{data.title}</h3>
                  <p style={{ color: '#666', fontSize: '13px', lineHeight: 1.8, margin: 0 }}>{data.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section style={{ padding: '80px 24px', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '12px' }}>COMPIFY</span>
            <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '900', letterSpacing: '1px' }}>{t.why.title}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            {t.why.cards.map((card, i) => (
              <div key={i} style={{ backgroundColor: '#1a1a1a', padding: '28px 20px', border: '1px solid #2a2a2a', position: 'relative' }}>
                {[
                  { top: 0, left: 0, borderTop: '1px solid #FF0066', borderLeft: '1px solid #FF0066' },
                  { bottom: 0, right: 0, borderBottom: '1px solid #FF0066', borderRight: '1px solid #FF0066' },
                ].map((c, j) => <div key={j} style={{ position: 'absolute', width: '10px', height: '10px', ...c }} />)}
                <span style={{ fontSize: '28px', display: 'block', marginBottom: '16px' }}>{card.icon}</span>
                <h3 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', color: '#fff' }}>{card.title}</h3>
                <p style={{ color: '#555', fontSize: '12px', lineHeight: 1.7, margin: 0 }}>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANNEQUIN VS COMEDIEN */}
      <section style={{ padding: '80px 24px', backgroundColor: '#0a0a0a', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '12px' }}>PROFILS</span>
            <h2 style={{ margin: '0 0 12px', fontSize: '32px', fontWeight: '900', letterSpacing: '1px' }}>{t.diff.title}</h2>
            <p style={{ color: '#555', fontSize: '13px', letterSpacing: '1px', margin: 0 }}>{t.diff.sub}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {[t.diff.mannequin, t.diff.comedien].map((prof, i) => (
              <div key={i} style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', overflow: 'hidden' }}>
                <div style={{ backgroundColor: '#FF0066', padding: '16px 28px' }}>
                  <span style={{ color: '#fff', fontSize: '13px', fontWeight: '900', letterSpacing: '3px' }}>{prof.label}</span>
                </div>
                <div style={{ padding: '28px' }}>
                  {prof.points.map((point, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                      <span style={{ color: '#FF0066', fontSize: '10px', flexShrink: 0 }}>▸</span>
                      <span style={{ color: '#888', fontSize: '13px', lineHeight: 1.5 }}>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '80px 24px', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', display: 'block', marginBottom: '12px' }}>FAQ</span>
            <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '900', letterSpacing: '1px' }}>{t.faq.title}</h2>
          </div>
          {t.faq.items.map((item, i) => (
            <div key={i} style={{ borderBottom: '1px solid #1a1a1a', marginBottom: '0' }}>
              <div onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', cursor: 'pointer' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px', color: openFaq === i ? '#FF0066' : '#fff' }}>{item.q}</h3>
                <span style={{ color: '#FF0066', fontSize: '18px', flexShrink: 0, marginLeft: '16px' }}>{openFaq === i ? '−' : '+'}</span>
              </div>
              {openFaq === i && (
                <p style={{ color: '#666', fontSize: '13px', lineHeight: 1.8, margin: '0 0 20px', paddingRight: '32px' }}>{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section style={{ padding: '100px 24px', textAlign: 'center', backgroundColor: '#0a0a0a' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 16px', fontSize: '40px', fontWeight: '900', letterSpacing: '2px', lineHeight: 1.1 }}>
            {t.cta.title} <span style={{ color: '#FF0066' }}>{t.cta.titleAccent}</span>
          </h2>
          <p style={{ color: '#555', fontSize: '13px', letterSpacing: '1px', margin: '0 0 40px', lineHeight: 1.8 }}>{t.cta.sub}</p>
          <button onClick={() => navigate('/editeur')} style={{
            backgroundColor: '#FF0066', color: '#fff', border: 'none',
            padding: '18px 48px', fontSize: '12px', fontWeight: '700',
            letterSpacing: '3px', cursor: 'pointer',
          }}>{t.cta.btn}</button>
        </div>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}